// src/routes/ProtectedRoute.jsx
import React, { useEffect } from "react";
import { Navigate, Outlet } from "react-router";
import { useDispatch } from "react-redux";
import { useAuthContext, useDialog } from "contexts";
import { panelApi, useGetCurrentUserQuery } from "store/api";
import { useQueryLoader } from "hooks";
import { formatError, logger } from "utils";

/**
 * Guard for every signed-in page.
 *
 * - Not signed in → /login
 * - Signed in → loads the user (RTK Query, getCurrentUser) unless login or
 *   signup already put it into the cache; the global loader shows meanwhile
 * - Loading the user fails → error dialog; closing it signs out
 * - Leaving (sign out) → the user and every cached list are dropped
 */
const ProtectedRoute = () => {
  const dispatch = useDispatch();
  const { showError } = useDialog();
  const { isAuthenticated, removeAccessToken } = useAuthContext();

  const { data, isFetching, error } = useGetCurrentUserQuery(undefined, {
    skip: !isAuthenticated,
  });
  useQueryLoader(isFetching);

  useEffect(() => {
    return () => {
      dispatch(panelApi.util.resetApiState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!error) return;
    logger.log("Error in ProtectedRoute:", error);
    showError(formatError(error), () => {
      removeAccessToken();
    });
  }, [error, removeAccessToken, showError]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!data?.id) {
    return null; // waiting for the user
  }

  return <Outlet />;
};

export default ProtectedRoute;
